import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, User, Phone, MapPin, ArrowRight, Loader2, Info } from "lucide-react";
import { useClientAuth } from "../context/ClientAuthContext";
import { toast } from "sonner";

export default function ClientRegister() {
  const { register } = useClientAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/account";

  const [form, setForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    password: "",
    telephone: "",
    sexe: "",
    adresse: "",
    code_postal: "",
    ville: "Istres",
    fiscal_email: "",
    urssaf_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setLoading(true);
    const payload = { ...form, fiscal_email: form.fiscal_email || null };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("Compte créé — bienvenue !");
      navigate(redirectTo, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <section className="bg-fegray-light px-6 py-16 lg:py-20">
      <div className="w-full max-w-2xl mx-auto fe-card p-8 lg:p-10" data-testid="client-register-card">
        <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-3">
          — Création de compte
        </p>
        <h1 className="text-3xl font-bold text-fegray-dark mb-2">Inscription</h1>
        <p className="text-sm text-fegray mb-8">
          Créez votre compte pour réserver vos séances et profiter du crédit d'impôt 50%.
        </p>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput icon={User} label="Prénom" name="prenom" value={form.prenom} onChange={set} required />
            <TextInput icon={User} label="Nom" name="nom" value={form.nom} onChange={set} required />
          </div>

          <TextInput icon={Mail} type="email" label="Email de connexion" name="email" value={form.email} onChange={set} required />
          <TextInput icon={Lock} type="password" label="Mot de passe (6+ caractères)" name="password" value={form.password} onChange={set} required />
          <TextInput icon={Phone} label="Téléphone" name="telephone" value={form.telephone} onChange={set} required />

          <div>
            <label className="block text-xs uppercase tracking-wider text-fegray mb-2">Civilité (optionnel)</label>
            <div className="flex gap-2" data-testid="register-sexe-group">
              {[
                { v: "F", l: "Femme" },
                { v: "M", l: "Homme" },
                { v: "NB", l: "Non-binaire" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set("sexe", form.sexe === o.v ? "" : o.v)}
                  data-testid={`register-sexe-${o.v}`}
                  className={`flex-1 py-2.5 rounded-full text-xs font-bold transition-all ${
                    form.sexe === o.v
                      ? "bg-fegreen text-white shadow"
                      : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <TextInput icon={MapPin} label="Adresse" name="adresse" value={form.adresse} onChange={set} />
            </div>
            <TextInput label="Code postal" name="code_postal" value={form.code_postal} onChange={set} />
            <div className="md:col-span-2">
              <TextInput label="Ville" name="ville" value={form.ville} onChange={set} />
            </div>
          </div>

          {/* Fiscal section */}
          <div className="bg-fegreen-light/40 border border-fegreen/20 rounded-2xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <Info className="w-5 h-5 text-fegreen mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-fegray-dark text-sm">
                  Crédit d'impôt 50% — Avance immédiate URSSAF
                </h3>
                <p className="text-xs text-fegray mt-1 leading-relaxed">
                  Renseignez l'email lié à votre compte fiscal (impots.gouv.fr) pour activer
                  l'avance immédiate URSSAF. Vous ne paierez que 50% du montant — l'URSSAF
                  reverse l'autre moitié à FORM'ELEC directement.
                </p>
              </div>
            </div>
            <TextInput
              icon={Mail}
              type="email"
              label="Email fiscal (URSSAF / impots.gouv.fr)"
              name="fiscal_email"
              value={form.fiscal_email}
              onChange={set}
              placeholder="email-impots@exemple.fr (optionnel)"
            />
            <label className="flex items-start gap-3 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={form.urssaf_consent}
                onChange={(e) => set("urssaf_consent", e.target.checked)}
                data-testid="register-urssaf-consent"
                className="mt-1 accent-fegreen w-4 h-4"
              />
              <span className="text-xs text-fegray-dark leading-relaxed">
                J'autorise FORM'ELEC à transmettre mes informations à l'URSSAF dans le cadre
                du dispositif <strong>avance immédiate Services à la Personne</strong>.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="client-register-submit"
            className="btn-green w-full disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Création du compte…</>
            ) : (
              <>Créer mon compte <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </form>

        <p className="text-sm text-fegray text-center mt-6">
          Déjà inscrit ?{" "}
          <Link
            to="/account/login"
            state={{ from: redirectTo }}
            className="text-fegreen font-semibold hover:underline"
            data-testid="link-to-login"
          >
            Connectez-vous
          </Link>
        </p>
      </div>
    </section>
  );
}

function TextInput({ icon: Icon, label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2">
        {label}{required && <span className="text-fegreen"> *</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fegray/50" />}
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          data-testid={`register-${name}`}
          className={`w-full bg-white border border-gray-200 rounded-xl ${Icon ? "pl-10" : "pl-4"} pr-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none transition`}
        />
      </div>
    </div>
  );
}
